import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { ArrowUpRight, Play, Users, DollarSign, MapPin, FileText } from 'lucide-react';
import { useState, useRef } from 'react';
import { api, cfmt } from '../../lib/api';

/**
 * Homepage · Southern Ontario Regional Hub
 *
 * Six large interactive region cards — each a mini-magazine dashboard with
 * live market snapshot, aggregated stats, an ambient video, latest report
 * link and four featured communities. Every card is a full-bleed hover
 * surface that links into that region's premium hub page.
 */

const humanPop = (p) => {
    if (!p) return '—';
    if (p >= 1000000) return `${(p / 1000000).toFixed(1)}M`;
    if (p >= 1000) return `${Math.round(p / 1000)}k`;
    return p.toString();
};

const RegionCard = ({ r, index }) => {
    const [videoOpen, setVideoOpen] = useState(false);
    const videoRef = useRef(null);
    const s = r.stats || {};

    return (
        <motion.article
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.6, delay: (index % 2) * 0.08, ease: [0.22, 0.61, 0.36, 1] }}
            className="border-r border-b border-neutral-200 bg-white overflow-hidden"
            data-testid={`region-card-${r.slug}`}
        >
            <div className="grid grid-cols-1 lg:grid-cols-12">
                {/* ==== Left · media panel ==== */}
                <div className="lg:col-span-5 relative bg-black min-h-[280px] lg:min-h-[520px]">
                    {videoOpen ? (
                        <video
                            ref={videoRef}
                            src={r.video_url}
                            controls
                            autoPlay
                            playsInline
                            className="absolute inset-0 w-full h-full object-cover"
                        />
                    ) : (
                        <>
                            <img
                                src={r.hero_image}
                                alt={r.name}
                                loading="lazy"
                                className="absolute inset-0 w-full h-full object-cover opacity-95"
                            />
                            <div
                                className="absolute inset-0 pointer-events-none"
                                style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0.25) 0%, rgba(0,0,0,0.1) 40%, rgba(0,0,0,0.75) 100%)' }}
                            />
                            {/* Rank */}
                            <div className="absolute top-4 left-4 font-mono-data text-[11px] tracking-[0.24em] uppercase text-white/80 tabular">
                                Region {String(index + 1).padStart(2, '0')}
                            </div>
                            {/* Video play button */}
                            {r.video_url && (
                                <button
                                    type="button"
                                    onClick={() => setVideoOpen(true)}
                                    data-testid={`region-video-play-${r.slug}`}
                                    className="absolute top-4 right-4 flex items-center gap-2 border border-white/70 text-white px-3 py-1.5 text-[11px] tracking-[0.18em] uppercase font-mono-data hover:bg-brand-gold hover:text-brand-navy transition-colors"
                                   
                                >
                                    <Play className="w-3 h-3" />
                                    Play reel
                                </button>
                            )}
                            {/* Name + position */}
                            <div className="absolute bottom-0 left-0 right-0 p-6 lg:p-8">
                                <div
                                    className="font-editorial text-white tracking-editorial leading-none"
                                    style={{ fontSize: 'clamp(40px, 4.4vw, 68px)', letterSpacing: '-0.02em' }}
                                >
                                    {r.name}
                                </div>
                                <div className="mt-3 font-editorial italic text-white/85 max-w-md leading-snug" style={{ fontSize: '15px' }}>
                                    {r.position}
                                </div>
                                {r.video_label && (
                                    <div className="mt-4 text-[10px] tracking-[0.24em] uppercase text-white/60 font-mono-data">
                                        {r.video_label}
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </div>

                {/* ==== Right · data panel ==== */}
                <div className="lg:col-span-7 flex flex-col">
                    {/* Market snapshot */}
                    <div className="p-6 lg:p-8 border-b border-neutral-200">
                        <div className="font-mono-data text-[11px] tracking-[0.22em] uppercase text-neutral-500">
                            Market Snapshot
                        </div>
                        <p className="mt-3 font-editorial text-black leading-relaxed" style={{ fontSize: '15.5px' }}>
                            {r.market_snapshot}
                        </p>
                        {r.sub_regions?.length > 0 && (
                            <div className="mt-4 flex flex-wrap gap-x-3 gap-y-1 text-[11px] tracking-[0.18em] uppercase text-neutral-500 font-mono-data">
                                {r.sub_regions.map((sr, i) => (
                                    <span key={sr}>
                                        {sr}{i < r.sub_regions.length - 1 && <span className="ml-3 text-neutral-300">·</span>}
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Stats grid */}
                    <div className="grid grid-cols-3 border-b border-neutral-200">
                        <StatCell
                            icon={<Users className="w-3.5 h-3.5" />}
                            label="Population"
                            value={humanPop(s.population)}
                            testid={`region-stat-pop-${r.slug}`}
                        />
                        <StatCell
                            icon={<DollarSign className="w-3.5 h-3.5" />}
                            label="Avg. home"
                            value={cfmt(s.avg_home_price)}
                            testid={`region-stat-price-${r.slug}`}
                            border
                        />
                        <StatCell
                            icon={<MapPin className="w-3.5 h-3.5" />}
                            label="Neighbourhoods"
                            value={s.neighbourhood_count}
                            testid={`region-stat-count-${r.slug}`}
                            border
                        />
                    </div>

                    {/* Featured communities */}
                    <div className="p-6 lg:p-8 border-b border-neutral-200 flex-1">
                        <div className="flex items-center justify-between">
                            <div className="font-mono-data text-[11px] tracking-[0.22em] uppercase text-neutral-500">
                                Featured Communities
                            </div>
                            <Link
                                to={r.hub_url}
                                className="text-[11.5px] tracking-[0.18em] uppercase text-neutral-500 hover:text-black font-mono-data flex items-center gap-1"
                            >
                                All <ArrowUpRight className="w-3 h-3" />
                            </Link>
                        </div>
                        <ul className="mt-4 border-t border-neutral-200">
                            {r.featured_communities?.map((c) => (
                                <li key={c.slug} className="border-b border-neutral-200">
                                    <Link
                                        to={`/neighbourhoods/${c.slug}`}
                                        data-testid={`region-featured-${r.slug}-${c.slug}`}
                                        className="flex items-baseline justify-between gap-4 py-2.5 group hover:text-black"
                                    >
                                        <span className="font-editorial text-[17px] tracking-editorial group-hover:italic">
                                            {c.name}
                                        </span>
                                        <span className="font-mono-data text-[12px] text-neutral-500 tabular whitespace-nowrap">
                                            {cfmt(c.median_price)}
                                            <span className="ml-3 text-[#2F6B47]">+{c.forecast_5yr_pct}%</span>
                                        </span>
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Latest report + hub CTA */}
                    <div className="p-6 lg:p-8 flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
                        <Link
                            to={r.latest_report?.url || '/insights'}
                            className="flex items-start gap-3 group"
                            data-testid={`region-report-${r.slug}`}
                        >
                            <div className="mt-0.5 w-8 h-8 border border-neutral-300 flex items-center justify-center group-hover:bg-brand-navy group-hover:text-white group-hover:border-brand-navy transition-colors">
                                <FileText className="w-4 h-4" />
                            </div>
                            <div>
                                <div className="font-mono-data text-[10px] tracking-[0.22em] uppercase text-neutral-500">
                                    Latest Report
                                </div>
                                <div className="mt-1 font-editorial text-black text-[15.5px] leading-tight group-hover:italic">
                                    {r.latest_report?.title || 'Regional overview'}
                                </div>
                                <div className="mt-1 text-[11px] text-neutral-500 font-mono-data uppercase tracking-[0.16em]">
                                    {r.latest_report?.meta}
                                </div>
                            </div>
                        </Link>
                        <Link
                            to={r.hub_url}
                            data-testid={`region-hub-cta-${r.slug}`}
                            className="inline-flex items-center gap-2 bg-brand-navy text-white px-5 py-3 text-[13px] font-medium hover:bg-neutral-800 whitespace-nowrap"
                            style={{ letterSpacing: '0.02em' }}
                        >
                            Explore {r.name}
                            <ArrowUpRight className="w-4 h-4" />
                        </Link>
                    </div>
                </div>
            </div>
        </motion.article>
    );
};


const StatCell = ({ icon, label, value, testid, border }) => (
    <div className={`p-6 ${border ? 'border-l border-neutral-200' : ''}`} data-testid={testid}>
        <div className="flex items-center gap-2 text-neutral-400">
            {icon}
            <span className="font-mono-data text-[10px] tracking-[0.22em] uppercase">{label}</span>
        </div>
        <div className="mt-3 font-editorial text-black tabular leading-none" style={{ fontSize: 'clamp(22px, 2.2vw, 30px)', letterSpacing: '-0.01em' }}>
            {value}
        </div>
    </div>
);


export default function SouthernOntarioHub() {
    const { data } = useQuery({
        queryKey: ['regions-overview'],
        queryFn: async () => (await api.get('/regions/overview')).data,
    });
    const items = data?.items || [];

    return (
        <section className="border-b border-neutral-200 bg-[#FAFAF8]" data-testid="southern-ontario-hub">
            <div className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-24 lg:pt-32 pb-20 lg:pb-28">
                <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-80px' }}
                    transition={{ duration: 0.7, ease: [0.22, 0.61, 0.36, 1] }}
                    className="max-w-3xl"
                >
                    <div className="flex items-center gap-3 text-[11px] tracking-[0.24em] uppercase text-neutral-500 font-mono-data">
                        <span className="inline-block w-6 h-px bg-neutral-400" />
                        Section 04 · The Southern Ontario Hub
                    </div>
                    <h2
                        className="font-editorial mt-6 leading-[1.03]"
                        style={{ fontSize: 'clamp(36px, 4.8vw, 68px)', letterSpacing: '-0.02em' }}
                        data-testid="southern-ontario-hub-title"
                    >
                        Six regions.
                        <br />
                        <span className="italic text-neutral-500">One honest map.</span>
                    </h2>
                    <p className="mt-6 font-editorial text-neutral-600 max-w-2xl" style={{ fontSize: 'clamp(16px, 1.15vw, 19px)', lineHeight: 1.55 }}>
                        Every intelligence hub is a full editorial dashboard — market snapshot, live stats,
                        an ambient reel, the latest report, and the communities worth watching this quarter.
                    </p>
                </motion.div>

                <div className="mt-14 lg:mt-20 grid grid-cols-1 gap-0 border-l border-t border-neutral-200">
                    {items.map((r, i) => (
                        <RegionCard key={r.slug} r={r} index={i} />
                    ))}
                </div>
            </div>
        </section>
    );
}
